import type { InitType } from '@intercom/messenger-js-sdk/dist/types'
import type { ClerkOrganization } from '@saaga/shared/interfaces/auth.interface'
import type { BillingTenant } from '@saaga/shared/interfaces/billing.interface'
import { logger } from '@saaga/shared/utils/logger.util'

const BILLING_API_URL = import.meta.env.VITE_GATEWAY_BILLING

export interface IntercomUserData {
  userId: string
  email: string | undefined
  firstName: string | null
  lastName: string | null
  createdAt: Date | null
}

export const fetchIntercomUserHash = async (
  token: string,
  tenantId: string,
  userId: string
): Promise<string | null> => {
  try {
    const response = await fetch(`${BILLING_API_URL}/intercom/user-hash`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-tenant-id': tenantId,
        'x-user-id': userId,
      },
    })

    if (!response.ok) {
      logger.warn('[Intercom] Failed to fetch user hash:', response.status)
      return null
    }

    const data = await response.json()
    return data.userHash
  } catch (err) {
    logger.warn('[Intercom] Error fetching user hash:', err)
    return null
  }
}

export const buildIntercomConfig = (
  appId: string,
  userData: IntercomUserData,
  userHash: string,
  organization: ClerkOrganization | null,
  tenant: BillingTenant | null
): InitType => {
  const userName = [userData.firstName, userData.lastName].filter(Boolean).join(' ') || undefined

  return {
    app_id: appId,
    user_id: userData.userId,
    user_hash: userHash,
    email: userData.email,
    name: userName,
    created_at: userData.createdAt ? Math.floor(userData.createdAt.getTime() / 1000) : undefined,
    ...(organization && buildCompanyData(organization)),
    ...(tenant && buildTenantData(tenant)),
    background_color: '#EB5201',
    action_color: '#EB5201',
  }
}

const buildCompanyData = (organization: ClerkOrganization): Record<string, unknown> => ({
  company: {
    company_id: organization.id,
    name: organization.name,
    ...(organization.createdAt && {
      created_at: Math.floor(organization.createdAt / 1000),
    }),
  },
})

const buildTenantData = (tenant: BillingTenant): Record<string, unknown> => ({
  subscription_plan: tenant.subscriptionName ?? undefined,
  subscription_status: tenant.subscriptionStatus ?? undefined,
})
